import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import classNames from 'classnames';
import { motion } from 'motion/react';
import {
  type DragEventHandler,
  type FC,
  type PropsWithChildren,
  useEffect,
  useRef,
  useState,
} from 'react';
import tinycolor from 'tinycolor2';
import FileDialog from '../../lib/FileDialog';
import { IconUpload } from '../BasicComponents/Icons';
import { type DraggedItem, type DropVerdict, evaluateDrop } from './evaluateDrop';

/** What the zone would do with the file currently held over it. */
export type DropZoneState = 'neutral' | 'accepted' | 'rejected';

export interface Props {
  onFilesDrop: (files: FileList) => void;
  onStateChange?: (state: DropZoneState) => void;
  className?: string;
  children: React.ReactNode;
  disabled?: boolean;
  allowedItemTypes?: Array<string>;
  allowedItemKind?: 'file' | string;
  showOverlay?: boolean;
  showOverlayAlways?: boolean;
  overlayText?: string;
  withFileDialog?: boolean;
}

const readItems = (dataTransfer: DataTransfer): Array<DraggedItem> =>
  Array.from(dataTransfer.items, (item) => ({ kind: item.kind, type: item.type }));

export const DropZone: FC<PropsWithChildren<Props>> = ({
  onFilesDrop,
  onStateChange,
  className,
  children,
  disabled = false,
  allowedItemKind,
  allowedItemTypes,
  showOverlay = true,
  showOverlayAlways = false,
  overlayText = 'Place your file here',
  withFileDialog = false,
  ...props
}) => {
  const neutral: DropVerdict = { accepted: !disabled, text: overlayText, variant: 'info' };
  const [verdict, setVerdict] = useState(neutral);
  // dragenter/dragleave bubble up from every child, so a plain boolean would flicker.
  const [depth, setDepth] = useState(0);

  const handleDragEnter: DragEventHandler = (e) => {
    e.preventDefault();
    setDepth((d) => d + 1);
    setVerdict(
      evaluateDrop({
        items: readItems(e.dataTransfer),
        overlayText,
        disabled,
        allowedItemKind,
        allowedItemTypes,
      }),
    );
  };

  const handleDragOver: DragEventHandler = (e) => {
    // Always default-prevented: without it the browser navigates to the dropped file. The
    // refusal is expressed through dropEffect instead.
    e.preventDefault();
    // `disabled` is read live rather than from the verdict, which was settled on dragenter and
    // would still say yes if the zone switched off mid-drag.
    e.dataTransfer.dropEffect = !disabled && verdict.accepted ? 'copy' : 'none';
  };

  const handleDragLeave: DragEventHandler = () => {
    setDepth((d) => {
      if (d <= 1) setVerdict(neutral);
      return Math.max(0, d - 1);
    });
  };

  const handleDrop: DragEventHandler = (e) => {
    e.preventDefault();
    setDepth(0);

    // Judge the files themselves rather than trusting the dragenter verdict: only now are real
    // File objects available, and browsers may have withheld the type until this point.
    const files = e.dataTransfer.files;
    const dropped = evaluateDrop({
      items: Array.from(files, (file) => ({ kind: 'file', type: file.type })),
      overlayText,
      disabled,
      allowedItemKind,
      allowedItemTypes,
    });

    setVerdict(dropped.accepted ? neutral : dropped);
    if (dropped.accepted) onFilesDrop(files);
  };

  const isOver = !disabled && depth > 0;
  // `accepted` means "this drop will work" and drives `file-hover`; `rejected` drives
  // `file-reject`. Consumers get the same three-way answer through onStateChange.
  const state: DropZoneState = !isOver ? 'neutral' : verdict.accepted ? 'accepted' : 'rejected';

  // Held in a ref so the effect can depend on `state` alone. Depending on the callback too would
  // re-fire it on every parent render that passes a fresh arrow function.
  const notify = useRef(onStateChange);
  notify.current = onStateChange;

  useEffect(() => {
    notify.current?.(state);
  }, [state]);

  const handleFileDialogRequest = () => {
    FileDialog.show({ accept: allowedItemTypes?.join(',') })
      .then(onFilesDrop)
      .catch((e) => console.error(e));
  };

  return (
    <Container
      className={classNames(
        { 'file-hover': state === 'accepted', 'file-reject': state === 'rejected' },
        className,
      )}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      {...props}
    >
      {children}
      {showOverlay && (
        <Overlay
          isOver={isOver}
          text={verdict.text}
          variant={verdict.variant}
          showAlways={showOverlayAlways}
          withFileDialog={withFileDialog}
          onFileDialogRequest={handleFileDialogRequest}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const Overlay: FC<{
  isOver: boolean;
  variant?: 'success' | 'danger' | 'warning' | 'info';
  text?: string;
  showAlways?: boolean;
  withFileDialog?: boolean;
  onFileDialogRequest: () => void;
}> = ({ isOver, variant = 'info', text, showAlways, withFileDialog, onFileDialogRequest }) => {
  const basicTransparency = showAlways ? 0.6 : 0;
  const color = getVariantColor(variant);
  const colorBackground = tinycolor(color).lighten(0.5).toHexString();

  const handleFileDialogClick: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
    e.preventDefault();
    onFileDialogRequest();
  };

  return (
    <OverlayBody
      initial={false}
      animate={{ opacity: isOver ? 0.95 : basicTransparency }}
      style={{ borderColor: color }}
    >
      <Background style={{ background: colorBackground }} />
      <IconUpload style={{ color }} />
      {text != null && <Text style={{ color }}>{text}</Text>}
      {withFileDialog && showAlways && (
        <ChooseFileLink onClick={handleFileDialogClick} style={{ color }}>
          ...or choose file
        </ChooseFileLink>
      )}
    </OverlayBody>
  );
};

const OverlayBody = styled(motion.div)`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  margin: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 50px;
  border-radius: 10px;
  border: dashed 4px ${RacemapColors.DarkGray};
  color: ${RacemapColors.DarkGray};
  pointer-events: none;
  z-index: 999;
`;

const Background = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background: black;
  opacity: 0.3;
  z-index: 0;
`;

const Text = styled.div`
  font-weight: 600;
  font-size: small;
`;

const ChooseFileLink = styled.a`
  font-size: small;
  color: ${RacemapColors.DarkBlue};
  text-decoration: underline;
  cursor: pointer;
  pointer-events: all;
`;

const getVariantColor = (variant: 'success' | 'danger' | 'warning' | 'info'): string => {
  switch (variant) {
    case 'success':
      return RacemapColors.BaseGreen;
    case 'danger':
      return RacemapColors.DarkRed;
    case 'warning':
      return RacemapColors.BaseYellow;
    default:
      return RacemapColors.PaleBlue;
  }
};
